/**
 * SỐ LIỆU ARENA PVE — TẦNG DỮ LIỆU, KHÔNG PHẢI TẦNG LUẬT.
 *
 * VÌ SAO FILE NÀY TỒN TẠI: người dùng yêu cầu Arena là **chế độ BỔ SUNG**, và số liệu cân bằng
 * phải sửa được mà KHÔNG phải động vào code mô phỏng. Tách dữ liệu ra đây thì:
 *   · người chỉnh cân bằng sửa 1 file, không phải đọc lại 1272 dòng vòng lặp 30Hz;
 *   · `sim/arena/world.ts` (task-1) vẫn là NGUỒN SỰ THẬT của LUẬT, còn file này là nguồn của SỐ.
 *
 * ⚠️⚠️ BẪY LỚN NHẤT CỦA FILE NÀY — ĐỌC TRƯỚC KHI THÊM BẤT KỲ CON SỐ NÀO.
 *
 * `sim/arena/world.ts` (task-1) ĐÃ khai sẵn: `ARENA_ECON` (giá tướng theo class · giá nông dân ·
 * sức chứa · tốc độ đào · trữ lượng mine · bán kính vào mine/ giao hàng), `ARENA_UNIT_DEF` (chỉ số
 * Elise · titan · nông dân), `ARENA_MINION_KIND`/ `ARENA_BOSS`/ `ARENA_TITAN`/ `ARENA_FARMER` (id),
 * `ARENA_CORE_MAX_HP`, `ARENA_WORLD`, `ARENA_LANE_Y`, `ARENA_CORE` (toạ độ Nhà), `ARENA_TICK`.
 *
 * **HAI NGUỒN SỰ THẬT CHO CÙNG MỘT CON SỐ LÀ LỖI NẶNG NHẤT CÓ THỂ TẠO RA Ở ĐÂY** — sửa một chỗ,
 * chỗ kia giữ số cũ, và triệu chứng lộ ra dưới dạng "trận đấu lạ" chứ không phải lỗi biên dịch.
 * Vì vậy file này KHÔNG chép lại bất cứ con số nào ở trên. Nó làm ĐÚNG 3 việc:
 *
 *   1. TÁI XUẤT (re-export) đúng những hằng số mà task-2 yêu cầu, trỏ thẳng về `world.ts`
 *      ⇒ `import { ARENA_LANE_Y } from '../data/arena'` và `... from '../sim/arena/world'` là
 *      CÙNG MỘT object. Không có bản sao nào để lệch.
 *   2. ĐỊNH NGHĨA phần còn thiếu, mà `world.ts` cố ý không có: `ARENA_HERO_COST` +
 *      `arenaHeroCost()` (API do task-2 chốt) và `ARENA_FARMER` (khối số liệu nông dân gộp một chỗ).
 *   3. GOM NHÃN TIẾNG VIỆT của Arena (`ARENA_TEXT`) — để `content-en-arena.ts` có ĐÚNG một bản VI
 *      để đối chiếu. ⚠️ Đây KHÔNG phải message của sim: xem chú thích `ARENA_TEXT`.
 *
 * ⚠️ FILE NÀY KHÔNG IMPORT PHASER và chỉ import `sim/arena/world` + `sim/types` (kiểu) từ tầng sim.
 * Dữ liệu thuần ⇒ `tests/arena-data.test.ts` chạy được trong môi trường node của vitest.
 *
 * ⚠️ BẪY NHỎ NHƯNG ĐÃ DÍNH: mọi `import` phải nằm ở ĐẦU file (ESM hoist import), kể cả import chỉ
 * dùng cho một `getter` ở cuối. Đặt `import` cạnh chỗ dùng cho "dễ đọc" là hợp lệ về cú pháp nhưng
 * làm người đọc sau tưởng nó chỉ có tác dụng cục bộ — và công cụ lint của repo sẽ kêu.
 */
import { CLASSES, type ClassId } from './classes';
import { ENEMIES, STAGE_BOSS, type EnemyDef } from './enemies';
import {
  ARENA_BOSS,
  ARENA_CORE,
  ARENA_CORE_MAX_HP,
  ARENA_CORE_Y,
  ARENA_ECON,
  ARENA_FARMER,
  ARENA_MAX_FARMERS,
  ARENA_MINE_YS,
  ARENA_MINION_KIND,
  ARENA_START_FARMERS,
  ARENA_TICK,
  ARENA_TITAN,
  ARENA_UNIT_DEF,
  ARENA_WORLD,
} from '../sim/arena/world';

// ════════════════════════════════════════════════════════════════════════════
// 1. TÁI XUẤT — CÁC HẰNG SỐ ĐÃ CÓ NHÀ, KHÔNG CHÉP LẠI
// ════════════════════════════════════════════════════════════════════════════

/*
 * `export { X } from '...'` KHÔNG tạo biến mới, không sao chép giá trị: nó chỉ mở lại đường dẫn
 * tới đúng chỗ khai gốc. Đây chính là cách tránh nguồn sự thật thứ hai.
 *
 * Đổi lại, 2 file phải cùng tồn tại (dữ liệu Arena không dùng độc lập được nếu thiếu sim). Đó là
 * lựa chọn CÓ Ý: thà phụ thuộc một chiều `data → sim/arena` còn hơn để 2 bản số liệu trôi khỏi nhau.
 *
 * ⚠️ ĐỪNG "CHO GỌN" BẰNG CÁCH KHAI LẠI SỐ Ở ĐÂY. Người dùng chốt `20 / 60 / 180 / 300` (02/10/2026:
 * *"đến phút thứ 1, tốc độ game x2 … phút thứ 3, boss cuối xuất hiện"* ⇒ quái khủng 240 → 180) và task-1
 * đã khoá chúng bằng test. Một bản sao ở đây sẽ khiến `tests/arena-data.test.ts` xanh trong khi
 * `ARENA_TICK` thật đã bị đổi — tức test khoá nhầm bản sao, đúng loại test vô dụng tệ nhất.
 */
export {
  ARENA_TICK,
  ARENA_WORLD,
  ARENA_CORE,
  ARENA_CORE_Y,
  ARENA_CORE_MAX_HP,
  ARENA_MINE_YS,
  ARENA_BOSS,
  ARENA_TITAN,
  ARENA_FARMER,
  ARENA_MAX_FARMERS,
  ARENA_MINION_KIND,
  ARENA_UNIT_DEF,
  ARENA_ECON,
} from '../sim/arena/world';

/**
 * MỎ TÀI NGUYÊN — 2 mỏ Ở 2 GÓC + 2 PHE, tổng 4 mỏ. Người dùng chốt: *"2 mine ở 2 góc"*.
 *
 * ⚠️ VÌ SAO KHÔNG CHÉP `x`/ `y` RA ĐÂY: toạ độ thật do `ArenaWorld` dựng trong constructor
 * (`mineY = ARENA_MINE_YS`, `mineX = [ARENA_CORE.leftX, ARENA_CORE.rightX]`) và task-3 vừa DỜI mỏ
 * ra khỏi dải cầu (xem `ARENA_MINE_YS`). Chép số ra đây là tự tay tạo nguồn sự thật thứ hai cho ĐÚNG
 * cái toạ độ mà test cần khoá.
 *
 * Nguồn sự thật vì thế là **`ArenaWorld.mines`** sau khi dựng — và `tests/arena-data.test.ts` dựng
 * một `ArenaWorld` 0 giây để KIỂM cả số lượng (đúng 4: 2 phe × 2 góc) lẫn "2 góc" (y khác nhau, 1
 * trên 1 dưới) so với `ARENA_MINE_YS`. Một chỗ đổi toạ độ là test đỏ ngay, mà không có con số nào bị
 * nhân đôi.
 *
 * ⚠️⚠️ THỨ TỰ MẢNG — BẪY ĐÃ DÍNH KHI VIẾT TEST, VẪN PHẢI GHI LẠI.
 * `world.ts` lặp `for (const y of mineY) for (const x of mineX)` nên mỏ ra theo TỪNG `y`:
 * `[40, 40, 560, 560]` (2 mỏ góc trên trước, rồi 2 mỏ góc dưới), KHÔNG xen kẽ trái/ phải. Bản trước
 * tôi đã đoán "0,2,0,2" và test đỏ — bài học: mảng này phải khớp THỨ TỰ của sim, không phải thứ tự
 * đọc cho đẹp. Còn `index` là số thứ tự cấp phát nên cũng chạy 0,1,2,3 theo đúng thứ tự này.
 *
 * ⚠️ HAI CHIỀU Y CỦA MỎ PHẢI NGOÀI MỌI DẢI CẦU — xem `ARENA_MINE_YS`. Nếu ai đó kéo mỏ vào dải cầu
 * thì `tests/arena-data.test.ts` có bài riêng bắt được (nông dân đứng đào giữa luồng quân qua cầu).
 * Còn `index`/ `amount`/ `owner` là trạng thái LÚC CHẠY (mỏ cạn dần, phe chiếm) nên không thể là dữ
 * liệu tĩnh — chúng nằm ở `ArenaMine` trong sim.
 */
export const ARENA_MINE_YS_DATA: readonly [number, number] = ARENA_MINE_YS;

/**
 * NHÀ CỦA 2 PHE Ở 2 MÉP TRÁI/ PHẢI — `x`/ `y` lấy thẳng từ `ARENA_CORE`, `hp` từ máu Nhà tối đa.
 *
 * ⚠️ ĐỌC KỸ CHIỀU CỦA `x` TRƯỚC KHI DÙNG — BẪY ĐÃ CÓ SẴN TRONG REPO:
 * `ARENA_CORE.leftX = 150` và `rightX = ARENA_WORLD.w − 150 = 2250`, nhưng Arena `2400×1000` là màn
 * NGANG nên "trái/ phải" ở đây là trục **x**, KHÔNG phải trục y như màn TD dọc cũ. Ai quen đọc
 * `World` cũ rất dễ tưởng `leftX` là "làn trái" theo y — sai hẳn.
 *
 * ⚠️ `getter` CỐ Ý: mỗi lần đọc trả object MỚI. `hp` ở đây là máu TỐI ĐA lúc dựng trận, KHÔNG phải
 * máu đang chạy — máu đang chạy nằm ở `ArenaWorld.cores[side].hp`. Trả chung một object cho mọi nơi
 * gọi là mời người ta sửa vào dữ liệu tĩnh rồi tưởng mình đã sửa trận đấu.
 */
export const ARENA_CORES: Record<'left' | 'right', { x: number; y: number; hp: number }> = {
  get left() {
    return { x: ARENA_CORE.leftX, y: ARENA_CORE.y, hp: ARENA_CORE_MAX_HP };
  },
  get right() {
    return { x: ARENA_CORE.rightX, y: ARENA_CORE.y, hp: ARENA_CORE_MAX_HP };
  },
};

// ════════════════════════════════════════════════════════════════════════════
// 2. GIÁ TƯỚNG — API CỦA TASK-2
// ════════════════════════════════════════════════════════════════════════════

/**
 * GIÁ TÀI NGUYÊN ĐỂ SUMMON TỪNG CLASS ĐÃ UNLOCK.
 *
 * ⚠️⚠️ `ARENA_ECON.heroPrice` (trong `world.ts`) ĐÃ CÓ BẢNG NÀY VÀ ĐANG ĐƯỢC DÙNG THẬT:
 * `summonHero()` đọc `ARENA_ECON.heroPrice[classId]` để trừ tài nguyên. Nên đây là
 * **BÍ DANH TRỎ VỀ ĐÚNG OBJECT ĐÓ**, không phải bảng giá thứ hai — sửa `world.ts` là giá đổi theo
 * ngay, vì cả hai tên cùng chỉ một object.
 *
 * Vì sao KHÔNG khai `Partial<Record<ClassId, number>>` bằng một object MỚI như mô tả task-2 (mô tả
 * đó viết trước khi task-1 tồn tại): `Partial` cho phép THIẾU khoá class, mà `world.ts` chú thích rõ
 * nó cố ý dùng `Record<ClassId, number>` để TypeScript **bắt lỗi khi thêm class mới mà quên đặt
 * giá**. Ép về `Partial` là nới lỏng đúng cái chốt an toàn đó, và tệ hơn: `summonHero` sẽ trừ
 * `undefined` nếu một class thiếu giá ⇒ tài nguyên thành `NaN` ⇒ kinh tế trận đấu hỏng CÂM LẶNG.
 *
 * Còn phần "class KHÔNG khai giá" mà task-2 yêu cầu thì `arenaHeroCost()` lo — nó có mặc định.
 */
export const ARENA_HERO_COST: Partial<Record<ClassId, number>> = ARENA_ECON.heroPrice;

/**
 * GIÁ MẶC ĐỊNH cho class KHÔNG khai trong `ARENA_HERO_COST`.
 *
 * Vì sao cần mặc định: danh sách class đã unlock là DỮ LIỆU NGƯỜI CHƠI (`meta.unlockedClasses`), nên
 * có thể xuất hiện id mà bảng giá chưa kịp có (class mới, save cũ, hoặc bảng giá bị tỉa bớt). Trả
 * `undefined` ở nhánh đó thì `resource -= undefined` = `NaN` ⇒ trận đấu chết câm lặng.
 *
 * Con số 100 = giá THẤP NHẤT trong bảng hiện tại (agility/ street). Cố ý chọn mức RẺ NHẤT thay vì
 * `Math.max`: class chưa có giá là class chưa được cân, mà mặc định đắt thì người chơi mở khoá xong
 * lại không mua nổi — bug khó hiểu hơn hẳn so với "hơi rẻ".
 */
export const ARENA_HERO_COST_DEFAULT = 100;

/**
 * GIÁ SUMMON 1 CLASS. LUÔN trả số hữu hạn > 0 cho MỌI `ClassId` — UI đọc số này để hiện giá lên nút.
 *
 * ⚠️ VÌ SAO DÙNG `Number.isFinite` CHỨ KHÔNG PHẢI `!= null`: một khoá khai `NaN` (từ `parseInt`
 * hỏng, hay `0/0`) lọt qua mọi phép so sánh `> 0`/ `< 0` và biến tài nguyên thành `NaN` mà KHÔNG
 * ném lỗi. Chặn ở đây là chặn ở đúng cửa duy nhất mà mọi nơi gọi đi qua.
 */
export function arenaHeroCost(classId: ClassId): number {
  const price = ARENA_HERO_COST[classId];
  return typeof price === 'number' && Number.isFinite(price) && price > 0 ? price : ARENA_HERO_COST_DEFAULT;
}

/** Giá summon của MỌI class — UI vẽ danh sách nút mà không phải tự lặp khoá. */
export function arenaHeroCosts(): Array<{ id: ClassId; cost: number }> {
  return (Object.keys(CLASSES) as ClassId[]).map((id) => ({ id, cost: arenaHeroCost(id) }));
}

// ════════════════════════════════════════════════════════════════════════════
// 3. NÔNG DÂN — KHỐI SỐ LIỆU GỘP MỘT CHỖ
// ════════════════════════════════════════════════════════════════════════════

/** Thời gian đào đầy 1 chuyến (giây) — SUY từ 2 hằng số gốc, xem chú thích `ARENA_FARMER`. */
export const ARENA_FARMER_MINE_TIME = ARENA_ECON.farmerCapacity / ARENA_ECON.minePerSecond;

/** Chỉ số nông dân trong registry — nguồn của `hp`/ `radius`/ `moveSpeed` bên dưới. */
const FARMER_DEF: EnemyDef = ENEMIES[ARENA_FARMER] ?? ARENA_UNIT_DEF[ARENA_FARMER] ?? STAGE_BOSS;

/**
 * NÔNG DÂN — GIÁ · LƯỢNG MANG · THỜI GIAN ĐÀO 1 CHUYẾN · TỐC ĐỘ ĐI.
 *
 * ⚠️⚠️ ĐỌC KỸ TRƯỚC KHI SỬA — 6 TRƯỜNG DƯỚI ĐÂY LÀ **BÍ DANH**, KHÔNG PHẢI SỐ TỰ KHAI:
 *   · `cost`      → `ARENA_ECON.farmerPrice` (80). ⚠️ ĐÂY LÀ GIÁ **CƠ BẢN**.
 *     Giá THẬT người chơi trả là `farmerPrice + sốNôngDânĐangSống × farmerPriceStep` (40/ con) —
 *     xem `buyFarmer()`. Đừng lấy `ARENA_FARMER.cost` đi trừ tiền: sẽ trừ thiếu đúng phần tăng dần
 *     và phá luật "nông dân có trần mềm". Cần giá thật thì gọi `arenaFarmerCost(aliveCount)`.
 *   · `carry`     → `ARENA_ECON.farmerCapacity` (30). Sức chứa 1 chuyến.
 *   · `mineTime`  → `farmerCapacity / minePerSecond` = 30 / 5 = **6 giây** đào đầy bao.
 *     SUY RA chứ không khai 6: đổi nhịp đào thì phải đổi `minePerSecond`, mà nếu ở đây ghi cứng 6
 *     thì hai chỗ lệch nhau trong im lặng.
 *   · `moveSpeed`/ `hp`/ `radius` → `ARENA_UNIT_DEF.farmer.base.moveSpeed` (132) / `.hp` (150) /
 *     `.radius` (10). Đọc qua registry nên mọi lần chỉnh chỉ số nông dân chảy thẳng xuống đây.
 *
 * Vì sao VẪN có khối này dù từng trường đều là bí danh: người chỉnh cân bằng nông dân cần ĐỌC một
 * chỗ ra đủ 6 số, thay vì nhảy 3 file. Cái giá phải trả là một lớp `getter` — rẻ hơn hẳn so với một
 * bảng số liệu thứ hai trôi khỏi bảng gốc.
 *
 * ⚠️ `atk = 0` và `range = 0` của nông dân là CỐ Ý (xem `ARENA_UNIT_DEF`): cho nó đánh nhau là
 * `pickTarget` kéo nó vào combat giữa đường ⇒ kinh tế chết. Đừng "cân bằng" bằng cách thêm `atk`.
 */
export const ARENA_FARMER_STATS: {
  readonly cost: number;
  readonly costStep: number;
  readonly carry: number;
  readonly mineTime: number;
  readonly moveSpeed: number;
  readonly hp: number;
  readonly radius: number;
} = {
  get cost() {
    return ARENA_ECON.farmerPrice;
  },
  get costStep() {
    return ARENA_ECON.farmerPriceStep;
  },
  get carry() {
    return ARENA_ECON.farmerCapacity;
  },
  get mineTime() {
    return ARENA_FARMER_MINE_TIME;
  },
  get moveSpeed() {
    return FARMER_DEF.base.moveSpeed;
  },
  get hp() {
    return FARMER_DEF.base.hp;
  },
  get radius() {
    return FARMER_DEF.radius;
  },
};

/**
 * Giá THẬT của nông dân tiếp theo khi đang có `aliveCount` nông dân sống — CÙNG công thức
 * `buyFarmer()` đang dùng. `getter` của `ARENA_FARMER_STATS.cost` KHÔNG trả giá này (nó là giá cơ
 * bản), nên UI phải gọi hàm ở đây; tách ra để không ai vô tình trừ thiếu tiền.
 */
export function arenaFarmerCost(aliveCount: number): number {
  return ARENA_FARMER_STATS.cost + Math.max(0, aliveCount) * ARENA_FARMER_STATS.costStep;
}

// ════════════════════════════════════════════════════════════════════════════
// 4. CHỈ SỐ QUÂN — CÙNG HÌNH DẠNG, TRỎ VỀ `ARENA_UNIT_DEF`
// ════════════════════════════════════════════════════════════════════════════

/**
 * HÌNH DẠNG CHỈ SỐ QUÂN CỦA ARENA (task-2 chốt). `kind` = id tra registry, đúng thứ tự lùi mà
 * `unitDef()` trong `world.ts` đang tra: `ENEMIES` trước, `ARENA_UNIT_DEF` sau.
 */
export interface ArenaUnitStats {
  /** id quái — khoá tra `ENEMIES` trước, `ARENA_UNIT_DEF` sau. */
  readonly kind: string;
  readonly hp: number;
  readonly atk: number;
  readonly def: number;
  readonly atkSpeed: number;
  readonly range: number;
  readonly moveSpeed: number;
  readonly radius: number;
  /** tên hiển thị TIẾNG VIỆT — bản EN ở `content-en-arena.ts`, tra qua `arenaUnitName()`. */
  readonly name: string;
}

/**
 * ĐỔI `EnemyDef` → `ArenaUnitStats`. Một HÀM, không phải một bảng: mọi lần chỉnh chỉ số trong
 * `ARENA_UNIT_DEF` chảy xuống đây tự động, không có bản sao nào để lệch.
 *
 * ⚠️ LẤY CHỈ SỐ GỐC (`def.base.*`), KHÔNG nhân `ARENA_STAT_MULT`/ `ARENA_DMG.skillMult` — hai hệ số
 * đó là LUẬT CỦA TRẬN, `spawnUnit()` nhân chúng lúc ra quân. Nhân sẵn ở đây là nhân hai lần (máu
 * titan phình gấp bội) và làm mọi phép cân bằng sau này không còn suy ra được từ dữ liệu.
 *
 * ⚠️ LẤY `name` TỪ `def.name` chứ không từ `ARENA_UNIT_NAME` của `world.ts`: bảng đó chỉ có 3 khoá
 * (elise/ titan/ farmer), thiếu `thug` (lính, id dùng chung với game TD). Lấy từ registry thì `thug`
 * ra đúng "Đầu Gấu Xóm" — cùng tên mà toast của `summonHero` đang in.
 */
function statsOf(def: EnemyDef): ArenaUnitStats {
  return {
    kind: def.id,
    hp: def.base.hp,
    atk: def.base.atk,
    def: def.base.def,
    atkSpeed: def.base.atkSpeed,
    range: def.base.range,
    moveSpeed: def.base.moveSpeed,
    radius: def.radius,
    name: def.name,
  };
}

/**
 * LÍNH — Nhà thả ra CẢ 3 land mỗi 20s. Dùng thẳng id `thug` (Đầu Gấu Xóm), KHÔNG tạo id mới: nó đã
 * có art AI (`art/ai/enemy-thug.png`) và combo võ trong `data/combat-moves.ts`. Xem chú thích
 * `ARENA_MINION_KIND` trong `world.ts`.
 *
 * ⚠️ `kind` lấy từ hằng số `ARENA_MINION_KIND` (đang = `'thug'`), KHÔNG hard-code chuỗi `'thug'`:
 * ai đổi lính sang loại quái khác thì bảng này đi theo ngay.
 */
export const ARENA_MINION: ArenaUnitStats = statsOf(ENEMIES[ARENA_MINION_KIND] ?? STAGE_BOSS);

/** BOSS **ELISE** — ra mỗi 60s, id riêng `'elise'` (không có trong `ENEMIES`). */
export const ARENA_BOSS_ELISE: ArenaUnitStats = statsOf(ARENA_UNIT_DEF[ARENA_BOSS] ?? STAGE_BOSS);

/**
 * QUÁI KHỦNG (BOSS CUỐI) — ra mỗi 180s (phút thứ 3), id riêng `'titan'`.
 * ⚠️ `hp 7200` là chỉ số GỐC: `spawnUnit` còn nhân `ARENA_STAT_MULT.titan.hp` nữa, nên máu thật
 * trong trận ≠ con số này. Đừng lấy số ở đây để tính "bao lâu thì phá được nó".
 */
export const ARENA_TITAN_STATS: ArenaUnitStats = statsOf(ARENA_UNIT_DEF[ARENA_TITAN] ?? STAGE_BOSS);

/** Bảng tra chỉ số theo `kind` — gom 3 khối trên cho UI/ test lặp một lần. */
export const ARENA_UNIT_STATS: Record<string, ArenaUnitStats> = {
  [ARENA_MINION.kind]: ARENA_MINION,
  [ARENA_BOSS_ELISE.kind]: ARENA_BOSS_ELISE,
  [ARENA_TITAN_STATS.kind]: ARENA_TITAN_STATS,
};

// ════════════════════════════════════════════════════════════════════════════
// 5. CÂN BẰNG — NHỊP + KINH TẾ
// ════════════════════════════════════════════════════════════════════════════

/**
 * NHỊP THẢ QUÂN + KINH TẾ — GOM MỘT CHỖ CHO NGƯỜI CHỈNH CÂN BẰNG.
 *
 * ⚠️⚠️ MỌI TRƯỜNG DƯỚI ĐÂY LÀ **BÍ DANH**, KHÔNG PHẢI SỐ TỰ KHAI. `getter` (không phải object
 * thường) vì đọc `ARENA_BALANCE.minionEvery` phải luôn ra giá trị SỐNG của `ARENA_TICK`, kể cả sau
 * khi HMR nạp lại module. Khai lại số ở đây là tạo đúng cái nguồn sự thật thứ hai mà file này sinh
 * ra để tránh — và tệ nhất là `tests/arena-data.test.ts` sẽ khoá BẢN SAO (20/60/300 vẫn xanh) trong
 * khi nhịp thật đã bị đổi.
 */
export const ARENA_BALANCE: {
  readonly minionEvery: number;
  readonly bossEvery: number;
  readonly titanEvery: number;
  readonly matchSeconds: number;
  /** tài nguyên đầu trận MỖI PHE (ví RIÊNG của Arena, KHÔNG phải `gold` của game TD) */
  readonly startResource: number;
  /** tài nguyên 1 nông dân vác về trong 1 chuyến */
  readonly mineYield: number;
  /** tài nguyên mỗi giây khi nông dân đang đứng đào */
  readonly minePerSecond: number;
  /** tổng trữ lượng 1 mỏ — cạn thì nông dân đứng chờ, KHÔNG kẹt vòng lặp */
  readonly mineAmount: number;
  /** số nông dân có sẵn lúc mở màn MỖI PHE */
  readonly startFarmers: number;
  /** máu Nhà — cửa thắng của cả trận (task-7 đang cân cho trận dài ~5 phút) */
  readonly coreHp: number;
  /** giá tướng RẺ NHẤT trong bảng — UI dùng để hiện "từ N tài nguyên" */
  readonly cheapestHero: number;
} = {
  get minionEvery() {
    return ARENA_TICK.minionEvery;
  },
  get bossEvery() {
    return ARENA_TICK.bossEvery;
  },
  get titanEvery() {
    return ARENA_TICK.titanEvery;
  },
  get matchSeconds() {
    return ARENA_TICK.matchSeconds;
  },
  get startResource() {
    return ARENA_ECON.start;
  },
  get mineYield() {
    return ARENA_ECON.farmerCapacity;
  },
  get minePerSecond() {
    return ARENA_ECON.minePerSecond;
  },
  get mineAmount() {
    return ARENA_ECON.mineAmount;
  },
  get startFarmers() {
    return ARENA_START_FARMERS;
  },
  get coreHp() {
    return ARENA_CORE_MAX_HP;
  },
  get cheapestHero() {
    return Math.min(...Object.values(ARENA_ECON.heroPrice));
  },
};

/**
 * NHỊP TUA TỰ ĐỘNG THEO MỐC THỜI GIAN TRẬN — yêu cầu người dùng 02/10/2026:
 * *"đến phút thứ 1, tốc độ game x2 … phút thứ 3 x3 game speed cho tôi thay vì phút thứ 4"*;
 * rồi 03/10/2026: *"trong 10s cuối, speed nâng lên thành x5"*.
 *
 * ⚠️ ĐÂY LÀ NHỊP TRÌNH BÀY, KHÔNG PHẢI LUẬT CỦA SIM: nó chỉ quyết định mỗi giây THỰC chạy bao nhiêu
 * tick, còn `world.time`/ hồi chiêu/ nhịp thả quân vẫn đếm bằng GIÂY TRẬN như cũ. Nhờ vậy trận vẫn
 * đúng 300 giây trận (mọi phép cân bằng giữ nguyên) nhưng chơi thực chỉ mất ~2 phút rưỡi.
 *
 * 🔢 MỐC: 0s ⇒ ×1 · 60s (phút 1) ⇒ ×2 · **180s (phút 3) ⇒ ×3** — trùng ĐÚNG mốc quái khủng ra sân
 * (`ARENA_TICK.titanEvery`) và mốc mọi tướng tự niệm ultimate (`ARENA_ULT_START_AT`), nên pha cuối
 * trận (khủng + ult) vừa nhanh hơn vừa dồn dập hơn. Bản cũ để 240s (phút 4) — người dùng đã sửa lại.
 * Một nguồn sự thật cho CẢ scene (tích luỹ tick) LẪN HUD (hiện "×2"/"×3" cạnh đồng hồ) — xem
 * `arenaTimeSpeedMul`.
 *
 * 🔢 **×5 Ở 10 GIÂY CUỐI** (`ARENA_SPEED_FINAL_WINDOW`/ `_MUL`): cửa sổ này KHÔNG nằm trong
 * `ARENA_SPEED_RAMP` vì nó tính theo `matchSeconds - t` (phải biết độ dài trận), không theo mốc tuyệt
 * đối — người dùng có thể đổi độ dài trận mà vẫn muốn "10 giây cuối" đúng nghĩa. Vì vậy
 * `arenaTimeSpeedMul` nhận thêm tham số thứ hai `matchSeconds`; **thiếu tham số đó thì KHÔNG có ×5**
 * (giữ nguyên hành vi cũ cho mọi chỗ gọi 1 tham số, kể cả test cũ).
 */
/**
 * MỐC BẬT ×3 (giây trận) — PHÚT THỨ 3 = 180s. Người dùng: *"phút thứ 3 x3 game speed cho tôi thay vì
 * phút thứ 4"*.
 *
 * ⚠️⚠️ ĐÂY LÀ BẢN SAO CÓ KHOÁ CỦA HAI MỐC KHÁC Ở TẦNG SIM (`ARENA_TICK.titanEvery` và
 * `ARENA_ULT_START_AT` — cả hai đều 180). `data/arena.ts` KHÔNG được import `sim/arena/world.ts`
 * (vòng import: world import data/arena). Vì vậy PHẢI có một bài test đối chiếu ba số này
 * (`tests/arena-data.test.ts`), nếu không thì lần sau ai đổi mốc quái khủng/ ult sẽ lệch âm thầm.
 */
export const ARENA_SPEED_X3_AT = 180;

export const ARENA_SPEED_RAMP: ReadonlyArray<{ at: number; mul: number }> = [
  { at: 0, mul: 1 },
  { at: 60, mul: 2 },
  { at: ARENA_SPEED_X3_AT, mul: 3 },
];

/** CỬA SỔ CUỐI TRẬN (GIÂY TRẬN) hưởng mức tua đặc biệt — người dùng chốt *"trong 10s cuối"*. */
export const ARENA_SPEED_FINAL_WINDOW = 10;
/** MỨC TUA TRONG CỬA SỔ CUỐI — người dùng: *"speed nâng lên thành x5"*. */
export const ARENA_SPEED_FINAL_MUL = 5;

/**
 * HỆ SỐ TUA TẠI GIÂY TRẬN `t` — tra từ `ARENA_SPEED_RAMP` (mốc lớn nhất đã qua), rồi áp cửa sổ cuối trận.
 *
 * `matchSeconds` là ĐỘ DÀI TRẬN: truyền vào thì `t >= matchSeconds - ARENA_SPEED_FINAL_WINDOW` ⇒ ×5.
 * ⚠️ KHÔNG truyền ⇒ chỉ còn thang `ARENA_SPEED_RAMP` (hành vi cũ) — chủ ý, để hàm này vẫn dùng được ở
 * chỗ không biết độ dài trận, và để mọi test cũ không bị đổi nghĩa.
 * ⚠️ HAI CHỖ GỌI THẬT đều truyền đủ 2 tham số: scene (`w.time`, `w.matchDuration`) và HUD
 * (`s.seconds`, `s.matchSeconds`) — cùng một nguồn, nên tốc độ chạy và nhãn "×5" không thể lệch.
 */
export function arenaTimeSpeedMul(t: number, matchSeconds?: number): number {
  let mul = 1;
  for (const step of ARENA_SPEED_RAMP) if (t >= step.at) mul = step.mul;
  if (matchSeconds !== undefined && t >= matchSeconds - ARENA_SPEED_FINAL_WINDOW) mul = ARENA_SPEED_FINAL_MUL;
  return mul;
}

// ════════════════════════════════════════════════════════════════════════════
// 6. NHÃN TIẾNG VIỆT — BẢN GỐC ĐỂ `content-en-arena.ts` DỊCH
// ════════════════════════════════════════════════════════════════════════════

/**
 * ⚠️⚠️ `ARENA_TEXT` **KHÔNG PHẢI** MESSAGE CỦA `sim/`. ĐỌC KỸ TRƯỚC KHI "NỐI CHO TIỆN".
 *
 * Message lỗi của Arena (`Không đủ tài nguyên (cần 120)`, `Chưa mở khoá Dân tập gym`, `Trận đã kết
 * thúc`…) sinh ra trong `sim/arena/world.ts` — chúng là DỮ LIỆU của sim và có test riêng của task-1
 * khoá nguyên văn. Tầng hiển thị phải dịch chúng qua `simMsg()` (`i18n/sim-messages.ts`), nơi có
 * bảng MẪU regex và quy ước *"không khớp mẫu nào thì trả nguyên văn, KHÔNG ném"*.
 *
 * Vì vậy `ARENA_TEXT` ở đây KHÔNG phải bảng message thứ hai, và KHÔNG được dùng để dịch message của
 * sim: nó là **NHÃN GIAO DIỆN + MẪU CÂU mà Arena dùng ở tầng UI** (tên land, nhãn tài nguyên, câu
 * nút bấm, và MẪU của các câu lỗi mà UI tự dựng bằng số liệu). Tách hai thứ ra bằng CHÚ THÍCH chứ
 * không bằng kiểu, vì cả hai đều là `string` — nên ai đọc file này phải đọc chú thích này.
 *
 * Quy ước repo giữ nguyên: **dữ liệu gốc là TIẾNG VIỆT**, bản EN tra theo **ID** — không nhân đôi
 * chữ Việt sang `content-en-arena.ts`. Đó là lý do mọi khoá dưới đây là id ổn định (`'resource'`,
 * `'msgNotEnough'`…), không phải câu chữ.
 *
 * ⚠️ QUY ƯỚC SỐ: các câu có số PHẢI giữ nguyên con số ở bản EN (`20`/ `60`/ `180`/ `300`/ `120`/
 * `5`) — `tests/arena-data.test.ts` đối chiếu từng cặp bằng `numbers()`, đúng cách `tests/i18n.test.ts`
 * đang khoá bản dịch chiêu. Đổi số ở bản VI mà quên bản EN là đỏ ngay.
 */
export const ARENA_TEXT = {
  /*
   * ── 2 BỜ SÔNG · 2 PHE · NHÀ ───────────────────────────────────────────────────────────────
   * ⚠️ 3 KHOÁ `landTop`/`landMiddle`/`landBot` ĐÃ BỊ XOÁ — luật chơi không còn "3 land".
   * `land` bị xoá khỏi tầng sim (xem đầu `sim/arena/types.ts`): sân nay chia bởi SÔNG + 2 CẦU, không
   * còn 3 dải ngang để đặt tên. Chúng từng được giữ như "DI SẢN CHỜ DỌN" vì `ARENA_TEXT_EN` phải phủ
   * đúng tập khoá của bảng này; nay đã xoá ĐỒNG THỜI ở cả hai bảng cùng `ARENA_LAND_NAME_EN` và các
   * assertion tương ứng ⇒ hợp đồng i18n vẫn kín, không còn khoá chết.
   */
  sideLeft: 'Phe ta',
  sideRight: 'Phe địch',
  core: 'Nhà chính',
  // ── tài nguyên + kinh tế
  resource: 'Tài nguyên',
  mine: 'Mỏ tài nguyên',
  farmer: 'Nông dân',
  buyFarmer: 'Mua nông dân',
  carrying: 'Đang vác',
  // ── summon
  summon: 'Triệu hồi',
  cost: 'Giá',
  // ── đồng hồ trận + kết quả
  matchTime: 'Thời gian',
  score: 'Điểm',
  statusPrep: 'Chuẩn bị',
  statusFighting: 'Đang đánh',
  statusWon: 'Thắng',
  statusLost: 'Thua',
  statusDraw: 'Hoà',
  // ── nhịp thả quân — số PHẢI khớp `ARENA_BALANCE` (xem `arenaCadenceLabels()`)
  waveEvery: 'Lính mỗi 20 giây',
  bossEvery: 'Elise mỗi 60 giây',
  titanEvery: 'Quái khủng mỗi 180 giây',
  matchLength: 'Trận dài 300 giây',
  // ── MẪU CÂU UI (KHÔNG phải message của sim — xem chú thích đầu mục)
  msgNotEnough: 'Không đủ tài nguyên (cần 120)',
  msgClassLocked: 'Chưa mở khoá Dân tập gym',
  msgFieldFull: 'Sân đã đầy 5 tướng',
  msgMatchOver: 'Trận đã kết thúc',
  /*
   * ⚠️ `msgHeroDeployed` ĐÃ BỊ XOÁ — mẫu câu của API CŨ (`summonHero(classId, land)`, câu "… ra land
   * giữa"). API thật nay là `summonHero(classId, x, y)` và message THẬT do sim sinh ra, dịch qua
   * `simMsg()` (`src/i18n/sim-messages.ts`) chứ KHÔNG qua bảng này. Giữ lại chỉ tạo một nguồn chữ thứ
   * hai không ai đọc.
   */
} as const;
/** Khoá hợp lệ của `ARENA_TEXT` — `content-en-arena.ts` phải phủ HẾT, thiếu là `typecheck` đỏ. */
export type ArenaTextKey = keyof typeof ARENA_TEXT;

/**
 * NHÃN NHỊP THẢ QUÂN — SINH TỪ `ARENA_BALANCE`, không chép số.
 * Dùng khi UI muốn luôn khớp dữ liệu sống; `ARENA_TEXT.waveEvery`… là bản dịch cố định và có test
 * khoá đúng 20/60/180/300.
 */
export function arenaCadenceLabels(): { minion: string; boss: string; titan: string; match: string } {
  return {
    minion: `Lính mỗi ${ARENA_BALANCE.minionEvery} giây`,
    boss: `Elise mỗi ${ARENA_BALANCE.bossEvery} giây`,
    titan: `Quái khủng mỗi ${ARENA_BALANCE.titanEvery} giây`,
    match: `Trận dài ${ARENA_BALANCE.matchSeconds} giây`,
  };
}

/**
 * ⚠️ `ARENA_LAND_NAME` ĐÃ BỊ XOÁ — KHÔNG KHÔI PHỤC.
 *
 * Bản cũ export một mảng 3 tên land (`['Land trên','Land giữa','Land dưới']`) để HUD và lớp vẽ gắn
 * nhãn cho 3 dải ngang. `land` đã bị xoá khỏi tầng sim (xem đầu `sim/arena/types.ts`): sân nay chia
 * bởi SÔNG + 2 CẦU, không còn dải nào để đặt tên. Giữ mảng này lại là giữ một API trỏ vào khái niệm
 * không tồn tại — tầng UI sẽ gọi nó và nhận về 3 cái tên vô nghĩa mà KHÔNG có lỗi biên dịch nào.
 *
 * ⚠️ Nếu tầng UI cần nhãn cho 2 BỜ SÔNG (ví dụ "Bờ ta"/"Bờ địch") thì thêm khoá MỚI vào `ARENA_TEXT`
 * — đừng dựng lại mảng 3 phần tử để "cho khớp chỗ cũ".
 */

/**
 * TÊN ĐƠN VỊ TIẾNG VIỆT theo `kind` — tra `ENEMIES` trước, `ARENA_UNIT_DEF` sau (đúng thứ tự của
 * `unitDef()`), cuối cùng lùi về chính `kind` để KHÔNG BAO GIỜ trả `undefined` cho UI.
 */
export function arenaUnitName(kind: string): string {
  return ENEMIES[kind]?.name ?? ARENA_UNIT_DEF[kind]?.name ?? kind;
}
